import {
	type FilterValue,
	type InvestmentProjectedListEntry,
	type InvestmentStatus,
	type ListInvestmentsDTO,
	type MetricEntity,
	type MetricParameters,
	type MetricReferenceType,
	type MetricResultEntry,
	FieldKey,
} from "@eu.mdotm.api/sphere";
import {
	ButtonGroupRadio,
	CircularProgressBar,
	DebouncedSearchInput,
	Icon,
	LocalOverlay,
	NestedTransition,
	type Option,
	type OrderBy,
	Pagination,
	type PaginationRequest,
	type PaginationResponse,
	ProgressBar,
	Transition,
	type TransitionStatus,
} from "@mdotm/shadcn/legacy/components";
import { type MultiSelectCtx, useMultiSelect } from "@mdotm/shadcn/legacy/headless";
import {
	type ClassList,
	generateUniqueDOMId,
	overrideClassList,
	toClassName,
	useDebounced,
	useDebouncedMemo,
	useDeepEqualEffect,
} from "@mdotm/shadcn/legacy/react-extensions";
import { useAtom } from "@mdotm/shadcn/legacy/reactivity";
import { noop, stableEmptyArray, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { type BulkActionDropdownItem, type BulkActionItem, BulkActions, Switch } from "@mdotm/shadcn/mdotm/components";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useQuery } from "@tanstack/react-query";
import type { ColDef, GetContextMenuItems, GridApi, GridOptions, IRowNode } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import type { HighchartsReactRefObject } from "highcharts-react-official";
import { Map, OrderedMap, Set } from "immutable";
import { type ForwardedRef, type ReactNode, type Ref, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import AuthorizationGuard, { hasAccess } from "$root/components/AuthorizationGuard";
import { IconWalls } from "$root/components/IconWall";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useEventBus, useGroupEventBus } from "$root/event-bus";
import { aclByArea, roleByArea } from "$root/functional-areas/acl/checkers/all";
import { validateACLPermissions } from "$root/functional-areas/acl/checkers/shared";
import { useUpdateAccessListControlDialog } from "$root/functional-areas/acl/hook/useUpdateAccessListControlDialog";
import {
	type CompareOverlayProps,
	CompareOverlay,
	generateCompareItem,
} from "$root/functional-areas/compare-portfolio/CompareOverlay";
import type { MinimumCompareProps } from "$root/functional-areas/compare-portfolio/MinimumCompareProps";
import { unitOfMeasurement } from "$root/functional-areas/metrics";
import { bulkAnalysisRepository } from "$root/functional-areas/portfolio/bulk-analysis-repository";
import { portfolioEntityManagementActions } from "$root/functional-areas/portfolio/entity-management";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import PortfolioInsightPanel, {
	type PortfolioInsightPanelHandles,
} from "$root/functional-areas/portfolio-studio/InsightPanel";
import {
	emptyInsightPanelPreset,
	insightPanelSession$,
} from "$root/functional-areas/portfolio-studio/insightPanelSession";
import { usePortfolioStudioState } from "$root/functional-areas/portfolio-studio/PortfolioStudioStore";
import { PortfolioStudioSettingTabEnum } from "$root/functional-areas/portfolio-studio-settings/tab-enum";
import { exportTemplateIcon, exportTemplateLabel } from "$root/functional-areas/reports/export-templates";
import { reportRepository } from "$root/functional-areas/reports/repository";
import { storyfolioRepository } from "$root/functional-areas/storyfolio/repository";
import { useUserValue } from "$root/functional-areas/user";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { usePaginationTools } from "$root/hooks/usePaginationTools";
import { formatNumber } from "$root/localization/formatters";
import { AgGridWithModules, useAgGridRowListener } from "$root/third-party-integrations/ag-grid/ag-grid";
import { generateIntentId, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { queryClientKeys, typedQueryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import { type MetricComparisonChartProps, MetricComparisonChart } from "$root/ui-lib/charts/MetricComparisonChart";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { sphereColumnsDef } from "$root/ui-lib/table/column/platform-columns-definition";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { minutes, seconds, useSearchParams } from "$root/utils";
import { portfolioListOrderByName$ } from "../portfolio-studio-table-settings";
import { PortfolioStudioTab } from "../portfolio-studio-tabs";
import type { InvestmentItem } from "./const";
import { useCanSendToExecution } from "$root/functional-areas/portfolio/execution";
import { type BulkAnalysisActions, useBulkAnalysisActions } from "./portfolio-actions";

export type DefaultSelectionProps = { current: boolean; enhance: boolean };

export const PortfolioListViewMode = {
	mixed: "MIXED",
	chart: "CHART",
	table: "TABLE",
} as const;

export type PortfolioListViewMode = (typeof PortfolioListViewMode)[keyof typeof PortfolioListViewMode];

/** @internal — exported for tests */
export async function getPaginatedInvestmentList(
	params: PaginationRequest<
		{
			query?: string;
			sidePanelFilters?: FilterValue[];
			sortByParameters?: MetricParameters;
			sortMetricReferenceEntity?: MetricEntity;
			sortMetricReferenceType?: MetricReferenceType;
		},
		FieldKey
	>,
): Promise<PaginationResponse<ListInvestmentsDTO>> {
	const paginationResult = await portfolioRepository.paginatedList.fetchQuery({
		offset: params.skip,
		limit: params.take,
		searchName: params.filters?.query,
		...(params.orderBy?.length
			? {
					sortBy: params.orderBy.at(0)!.columnName,
					sortDirection: params.orderBy.at(0)?.direction === "asc" ? "ASC" : "DESC",
				}
			: {}),
		filters: params.filters?.sidePanelFilters,
		sortByParameters: params.filters?.sortByParameters,
		sortMetricReferenceEntity: params.filters?.sortMetricReferenceEntity,
		sortMetricReferenceType: params.filters?.sortMetricReferenceType,
	});

	return {
		items: paginationResult.items ?? [],
		total: paginationResult.pagination?.total ?? 0,
	};
}

function PortfolioList(): React.JSX.Element {
	const history = useHistory();
	const { preselectedInvestments: preselectedInvestmentsStr } = useSearchParams();
	const [preselectedInvestments] = useState(() => {
		if (!preselectedInvestmentsStr) {
			return null;
		}
		history.replace({
			...history.location,
			search: (() => {
				const parsed = new URLSearchParams(history.location.search);
				parsed.delete("preselectedInvestments");
				return parsed.toString();
			})(),
		});
		return preselectedInvestmentsStr.split("|");
	});

	const getProjectedList = useCallback((params?: { filters?: FilterValue[]; search?: string }) => {
		return portfolioRepository.projectedList.fetchQuery({
			filters: params?.filters,
			searchName: params?.search,
		});
	}, []);

	return (
		<div className="flex-1 min-h-0 flex flex-col relative z-0">
			<PortfolioListInner preselectedInvestments={preselectedInvestments} projectListProvider={getProjectedList} />
		</div>
	);
}

type PortfolioListInnerProps = {
	preselectedInvestments: string[] | null;
	projectListProvider(params?: { filters?: FilterValue[]; search?: string }): Promise<InvestmentProjectedListEntry[]>;
};

function PortfolioListInner({ preselectedInvestments, projectListProvider }: PortfolioListInnerProps) {
	const user = useUserValue();
	const { t } = useTranslation();
	const rowSelection = useMultiSelect<string>();

	const [comparedInvestments, setComparedInvestments] = useState<CompareOverlayProps["compareData"]>([]);

	const { portfolioStudioStore, setPortfolioStudioStore } = usePortfolioStudioState();
	const [portfolioListOrderByName, setPortfolioListOrderByName] = useAtom(portfolioListOrderByName$);
	const [insightPanelPreset, setInsightPanelPreset] = useAtom(insightPanelSession$);

	const firstRun = useRef(true);
	if (firstRun.current) {
		firstRun.current = false;
		if (preselectedInvestments) {
			setInsightPanelPreset((cur) => ({
				search: "",
				name: "Temporary selection",
				uuid: "temporary-selection",
				favorite: false,
				standard: false,
				metric: cur?.metric,
				filters: Map({
					[generateUniqueDOMId()]: {
						key: "NAME",
						type: "FINITE_SET_PORTFOLIOS",
						relation: "EQUAL",
						value: preselectedInvestments,
					},
				}),
			}));
		}
	}

	const portfolioInsightHandleRef = useRef<PortfolioInsightPanelHandles | null>(null);
	const chartRef = useRef<HighchartsReactRefObject | null>(null);
	const [gridEl, setGridEl] = useState<AgGridReact<InvestmentItem> | null>(null);

	useEffect(() => {
		if (!hasAccess(user, { requiredService: "BULK_ANALYSIS" })) {
			setPortfolioStudioStore({
				viewMode: "TABLE",
				isightPanel: { open: false },
			});
		}
	}, [setPortfolioStudioStore, user]);

	const viewMode = portfolioStudioStore.viewMode ?? "MIXED";
	function setViewMode(mode: PortfolioListViewMode) {
		setPortfolioStudioStore((ps) => ({ ...ps, viewMode: mode }));
	}

	const validSidePanelFilters = useDebouncedMemo<FilterValue[] | undefined>(
		() => {
			const filters = insightPanelPreset?.filters?.valueSeq().toArray();

			return (
				filters
					?.filter((filter) => filter.key && filter.relation && filter.value && filter.value.length > 0)
					.map(
						(x): FilterValue => ({
							key: x.key,
							relation: x.relation,
							values: x.value,
						}),
					) ?? []
			);
		},
		[insightPanelPreset?.filters],
		{ debounceInterval: 500 },
	);

	const validMetricReferenceEntity = useDebouncedMemo<MetricEntity | undefined>(
		() => {
			const referenceIdentifier = insightPanelPreset?.metric?.referenceEntity?.identifier;
			const referenceType = insightPanelPreset?.metric?.referenceEntity?.type;

			if (!referenceIdentifier || !referenceType) {
				return undefined;
			}

			return { identifier: referenceIdentifier, type: referenceType };
		},
		[insightPanelPreset?.metric?.referenceEntity?.identifier, insightPanelPreset?.metric?.referenceEntity?.type],
		{ debounceInterval: 500 },
	);

	const validMetricReferenceType = useDebouncedMemo<MetricReferenceType | undefined>(
		() => insightPanelPreset?.metric?.referenceType,
		[insightPanelPreset?.metric?.referenceType],
		{ debounceInterval: 500 },
	);

	const { take, skip, query } = usePaginationTools({ take: 20 });

	const queryInstrumentsRows = useQueryNoRefetch(
		[
			...portfolioRepository.paginatedList.fullQueryKey,
			portfolioListOrderByName,
			query.value,
			skip.value,
			take.value,
			validSidePanelFilters.value,
			validMetricReferenceEntity.value?.identifier,
			validMetricReferenceEntity.value?.type,
			validMetricReferenceType.value,

			insightPanelPreset?.metric?.metricParameters?.classificationUuid,
			insightPanelPreset?.metric?.metricParameters?.option,
			insightPanelPreset?.metric?.metricParameters?.riskModelExposureAssetClass,
			insightPanelPreset?.metric?.metricParameters?.riskModelExposureType,
			insightPanelPreset?.metric?.metricParameters?.riskModelSecondLevelExposureAssetClass,
			insightPanelPreset?.metric?.metricParameters?.riskModelSecondLevelExposureType,
		],
		{
			enabled: hasAccess(user, { requiredService: "BULK_ANALYSIS" }) ? insightPanelPreset !== undefined : true, // added since portfolioListOrderByName is cached, and some sort need also insightPanelPreset data
			queryFn() {
				return getPaginatedInvestmentList({
					skip: skip.value,
					take: take.value,
					orderBy: portfolioListOrderByName,
					filters: {
						query: query.value,
						sidePanelFilters: validSidePanelFilters.value,
						sortMetricReferenceEntity: validMetricReferenceEntity.value,
						sortMetricReferenceType: validMetricReferenceType.value,
						sortByParameters: insightPanelPreset?.metric?.metricParameters,
					},
				});
			},
			keepPreviousData: true,
			staleTime: 0,
			cacheTime: minutes(5),
		},
	);

	//introduce selection incoherence by resetting and when user add new filter
	const queryProjectInvestmentList = useQueryNoRefetch(
		[queryClientKeys.portfolioStudioProjectedInvestmentList, validSidePanelFilters.value],
		{
			queryFn() {
				return projectListProvider({
					filters: validSidePanelFilters.value,
					// search: query.value,
				});
			},
		},
	);

	useQueryReactor(queryProjectInvestmentList, {
		onSuccess(newInvestmentList) {
			const investmentUuids = Set(newInvestmentList.map((x) => x.uuid!));
			const newSelection = rowSelection.selection
				.filter((uuid) => investmentUuids.has(uuid))
				.valueSeq()
				.toArray();
			rowSelection.setSelection(newSelection);
		},
	});

	const queryUnfilteredProjectList = useQueryNoRefetch(
		[queryClientKeys.portfolioStudioProjectedInvestmentList, "unfilteredProjectedInvestmentList"],
		{
			queryFn() {
				return projectListProvider();
			},
		},
	);

	useGroupEventBus(
		"investment-import-update",
		() => {
			typedQueryClient().invalidateQueries([queryClientKeys.portfolioStudioProjectedInvestmentList]).catch(noop);
			portfolioRepository.invalidateLists();
		},
		{ timeout: seconds(3) },
	);

	useGroupEventBus(
		"investment-bulk-enhance-update",
		() => {
			typedQueryClient().invalidateQueries([queryClientKeys.portfolioStudioProjectedInvestmentList]).catch(noop);
			portfolioRepository.invalidateLists();
		},
		{ timeout: seconds(3) },
	);

	useGroupEventBus(
		"investment-update",
		(event) => {
			const someEventsArePortfolios = event.some(
				(x) => x.type && x.type !== "UNIVERSE_UPDATE" && x.type !== "REFERENCE_UPDATE" && x.type !== "NONE",
			);

			if (someEventsArePortfolios) {
				typedQueryClient().invalidateQueries([queryClientKeys.portfolioStudioProjectedInvestmentList]).catch(noop);
				portfolioRepository.invalidateLists();
			}
		},
		{ timeout: seconds(3) },
	);

	useEventBus("shared-entity", (e) => {
		if (e.level === "INFO" && e.sharedEntityType === "INVESTMENT") {
			typedQueryClient().invalidateQueries([queryClientKeys.portfolioStudioProjectedInvestmentList]).catch(noop);
			portfolioRepository.invalidateLists();
		}
	});

	useEventBus("shared-entities", (e) => {
		const investments = e.investments?.filter((x) => x.sharedEntityType === "INVESTMENT");
		if (investments?.length && investments?.length > 0) {
			typedQueryClient().invalidateQueries([queryClientKeys.portfolioStudioProjectedInvestmentList]).catch(noop);
			portfolioRepository.invalidateLists();
		}
	});

	const metricResultQuery = useQuery({
		...bulkAnalysisRepository.keyMetric.getQueryConfig(insightPanelPreset?.metric ?? null, {
			sortKey: portfolioListOrderByName.at(0)?.columnName,
			sortParameters: insightPanelPreset?.metric?.metricParameters, // will find params in body request but semantically it used just for sorting
			sortDirection: portfolioListOrderByName.at(0)?.direction === "asc" ? "ASC" : "DESC",
			filters: validSidePanelFilters.value,
			search: query.value,
		}),
		enabled: hasAccess(user, { requiredService: "BULK_ANALYSIS" }),
		keepPreviousData: true,
	});

	const { key: metricKey, metricParameters } = insightPanelPreset?.metric ?? {};

	useQueryReactor(metricResultQuery, {
		onSuccess(response) {
			if (!gridEl) {
				return;
			}

			if (!response) {
				const columnsState = [...(gridEl.api.getColumnState() ?? [])];
				if (columnsState && columnsState.length > 0) {
					const filteredColDefs = columnsState.filter((c) => c.colId !== "KEY_METRIC");
					gridEl.api.applyColumnState({
						state: filteredColDefs,
						applyOrder: true,
					});
				}

				return;
			}

			const columnsState = [...(gridEl.api.getColumnState() ?? [])];
			const keyMetricColumn = sphereColumnsDef.investmentKeyMetric<InvestmentItem>();
			const keyMetricAlreadyExist = columnsState.find((x) => x.colId === keyMetricColumn.colId);
			if (!keyMetricAlreadyExist) {
				const [first, ...rest] = columnsState;
				gridEl.api.applyColumnState({
					state: [
						first,
						{
							colId: keyMetricColumn.colId!,
							width: keyMetricColumn.width,
							pinned: keyMetricColumn.pinned,
						},
						...rest,
					],
					applyOrder: true,
				});
			}
		},
	});

	const keyMetricHeaderName = useMemo(
		() =>
			!metricKey
				? undefined
				: (metricParameters?.classificationUuid && metricParameters?.option) ||
					metricParameters?.riskModelExposureAssetClass ||
					t(`TABLE.HEADERS.${metricKey}`),
		[t, metricParameters, metricKey],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		gridEl?.api.refreshHeader();
	}, [keyMetricHeaderName]);

	function syncAgSelectionWithCtx(api: GridApi<InvestmentItem>, selectionCtx: MultiSelectCtx<string>) {
		api.deselectAll();
		api.forEachNode((node) => {
			if (node.data?.uuid && selectionCtx.selection.has(node.data.uuid)) {
				node.setSelected(true);
			}
		});
	}

	const isRestoringSelection = useRef<boolean>(false);
	useQueryReactor(queryInstrumentsRows, {
		onSuccess(data) {
			const api = gridEl?.api;
			if (!api) {
				console.warn("ag grid not ready yet");
				return;
			}

			isRestoringSelection.current = true;

			syncAgSelectionWithCtx(api, rowSelection);

			isRestoringSelection.current = false;
		},
	});

	const rows = useMemo(() => {
		const currentRows = queryInstrumentsRows.data?.items ?? [];
		const metrics = metricResultQuery.data?.metricResultEntries;
		if (!metrics || metrics.length === 0) {
			return currentRows;
		}

		return currentRows.map(
			(x): InvestmentItem => ({
				...x,
				metrics: createMetricCellValue(x, metrics, metricKey),
			}),
		);
	}, [queryInstrumentsRows.data, metricResultQuery.data, metricKey]);

	useAgGridRowListener(gridEl, "selectionChanged", (event) => {
		if (isRestoringSelection.current || queryInstrumentsRows.isFetching) {
			return;
		}
		if (event.selectedNodes?.length && event.selectedNodes?.length > 0) {
			trackMixPanelEvent("portfolio.bulkAnalysis.selectPage", {
				selectedPortfolios: event.selectedNodes?.length,
			});
		} else {
			trackMixPanelEvent("portfolio.bulkAnalysis.unselectPage", null);
		}

		const selectionToMerge = event.selectedNodes?.map((x) => x.data?.uuid).filter((x) => x != null) ?? [];
		const selectionToRemove = rows.filter((x) => x.uuid && !selectionToMerge.includes(x.uuid)).map((x) => x.uuid!);

		let newSelection = Set(rowSelection.selection.merge(selectionToMerge)); // add new selection
		if (selectionToRemove.length > 0) {
			for (const tickerName of selectionToRemove) {
				newSelection = newSelection.remove(tickerName);
			}
		}

		rowSelection.setSelection(newSelection);
	});

	const unfilteredProjectedPortfolios = useMemo(
		() => queryUnfilteredProjectList.data ?? [],
		[queryUnfilteredProjectList.data],
	);

	const unfilteredProjectedRowsMap = useMemo(
		() => Map(unfilteredProjectedPortfolios.filter((x) => x.uuid).map((x) => [x.uuid!, x])),
		[unfilteredProjectedPortfolios],
	);

	const filteredProjectedRowsMap = useMemo(
		() => Map(queryProjectInvestmentList.data?.filter((x) => x.uuid).map((x) => [x.uuid!, x])),
		[queryProjectInvestmentList.data],
	);

	const { invoke: reflowChart } = useDebounced(
		() => {
			const chart = chartRef.current?.chart;
			if (chart) {
				chart.reflow();
			}
		},
		{ debounceInterval: seconds(0.5) },
	);

	const { invoke: resetZoom } = useDebounced(
		() => {
			if (chartRef.current) {
				chartRef.current.chart.zoomOut();
			}
		},
		{ debounceInterval: seconds(0.5) },
	);

	useDeepEqualEffect(() => {
		noop(metricResultQuery.data); // track
		resetZoom();
	}, [metricResultQuery.data, resetZoom]);

	const { metricResultEntries } = metricResultQuery.data ?? {};
	const { value: metricResultsById } = useDebouncedMemo(
		() => OrderedMap(metricResultEntries?.map((entry) => [entry.uuid!, entry]) ?? []),
		[metricResultEntries],
	);

	const onExpandChange = useCallback(() => {
		setPortfolioStudioStore((ps) => {
			const isOpening = !ps.isightPanel?.open;
			if (isOpening) {
				trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.open", null);
			}
			return { ...ps, isightPanel: { open: isOpening } };
		});
		reflowChart();
	}, [reflowChart, setPortfolioStudioStore]);

	const onCompareInvestments = useCallback(
		(portfolio: MinimumCompareProps, enhanced?: boolean) =>
			setComparedInvestments((prev) => {
				const portfolioAlreadyCompared = prev.find((x) => x.uuid === portfolio.uuid && x.enhanced === enhanced);
				if (portfolioAlreadyCompared) {
					return prev.filter((x) => x.id !== portfolioAlreadyCompared.id);
				}

				return [...prev, generateCompareItem(portfolio, enhanced ?? false)];
			}),
		[],
	);

	const columns = columnsDefinition.usePortfolioList({
		compare: {
			onCompareInvestments,
			queryConfig: portfolioRepository.comparisonDetails.getQueryConfig,
		},
	});

	const onChangeOrder = useCallback(
		(newOrderBy: OrderBy<FieldKey>[]) => {
			setPortfolioListOrderByName(newOrderBy);
			const insightNewOrder = newOrderBy.at(0)?.columnName === insightPanelPreset?.metric?.key;
			const order = newOrderBy.at(0)?.direction;
			portfolioInsightHandleRef.current?.setOrder(!insightNewOrder ? "UNSORTED" : order === "asc" ? "ASC" : "DESC");
		},
		[insightPanelPreset?.metric?.key, setPortfolioListOrderByName],
	);

	useAgGridRowListener(gridEl, "sortChanged", (params) => {
		const sortedCol = params.columns?.filter((col) => col.getSort() != null).at(0);
		const keyMetricCol = sphereColumnsDef.investmentKeyMetric<InvestmentItem>();
		if (sortedCol) {
			const colKey = sortedCol.getColId();

			if (colKey === FieldKey.FixedWeights || colKey === FieldKey.LiveTrading) {
				return;
			}

			onChangeOrder([
				{
					columnName: (colKey === keyMetricCol.colId ? metricKey : colKey) as FieldKey,
					direction: sortedCol.getSort()!,
				},
			]);
		}
	});

	const onCompareMultipleInvestments = useCallback(
		(selected?: Map<string, DefaultSelectionProps>) => {
			setComparedInvestments((prev) => {
				const selectedPortfolios = Set<string>([...prev.map((x) => x.uuid ?? ""), ...(selected?.keys() ?? [])]);

				return selectedPortfolios.toArray().flatMap((uuid) => {
					const investment = unfilteredProjectedRowsMap?.get(uuid);
					if (!investment) {
						return [];
					}

					const preferences = selected?.get(uuid);
					const existingPortfolio = prev.filter((portfolio) => portfolio.uuid === uuid);
					const currentPortfolio = generateCompareItem(investment, false);
					const enhancedPortfolio = generateCompareItem(investment, true);
					if (existingPortfolio.length === 0) {
						return preferences === undefined
							? [currentPortfolio]
							: [
									...(preferences.current ? [currentPortfolio] : []),
									...(preferences.enhance ? [enhancedPortfolio] : []),
								];
					}

					return preferences === undefined
						? existingPortfolio
						: [
								...(preferences.current ? [existingPortfolio.find((x) => !x.enhanced) ?? currentPortfolio] : []),
								...(preferences.enhance ? [existingPortfolio.find((x) => x.enhanced) ?? enhancedPortfolio] : []),
							];
				});
			});
		},
		[unfilteredProjectedRowsMap],
	);

	const portfolioBulkActions = useBulkAnalysisActions(
		rowSelection.selection
			.toArray()
			.map((uuid) => unfilteredProjectedRowsMap.get(uuid)!)
			.filter((x) => x),
		{
			currentlyComparedInvestments: comparedInvestments.map(({ uuid }) => uuid!),
			setManualSelection: (uuids) => portfolioInsightHandleRef.current?.setManualSelection(uuids),
			setCompareMultipleInvestments: onCompareMultipleInvestments,
			resetMultiselectCtx: () => {
				gridEl?.api.deselectAll();
				rowSelection.reset();
			},
			onOpenNewTab(investmentsUuids) {
				return typedUrlForRoute("PortfoliosStudio", {
					tab: PortfolioStudioTab.Portfolios,
					preselectedInvestments: investmentsUuids.join("|"),
				});
			},
		},
	);

	const onApplyFilters = useCallback(
		(investments: Set<string>) => {
			portfolioInsightHandleRef.current?.setManualSelection(investments.toArray());
			rowSelection.reset();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[rowSelection],
	);

	if (queryUnfilteredProjectList.isLoading) {
		return <ProgressBar value="indeterminate" />;
	}

	if (queryUnfilteredProjectList.isError || queryInstrumentsRows.isError) {
		return (
			<IconWalls.ErrorData
				undoButton={{
					onClick: () => setInsightPanelPreset(emptyInsightPanelPreset),
					label: "Reset filters",
				}}
			/>
		);
	}

	if (queryUnfilteredProjectList.data?.length === 0) {
		return (
			<IconWalls.PortfolioListEmptyData
				refetch={async () => {
					await queryInstrumentsRows.refetch();
				}}
			/>
		);
	}

	return (
		<>
			<div className="min-h-0 grow flex relative z-0">
				<AuthorizationGuard requiredService="BULK_ANALYSIS">
					<PortfolioInsightPanel
						expand={portfolioStudioStore.isightPanel?.open ?? false}
						onExpandChange={onExpandChange}
						handleRef={portfolioInsightHandleRef}
						onChangePreset={(newPreset) => {
							if (newPreset?.search != null) {
								query.set(newPreset.search);
							}

							if (newPreset?.metric?.key && newPreset?.metric?.ordering !== "UNSORTED") {
								setPortfolioListOrderByName([
									{
										columnName: newPreset?.metric?.key,
										direction: newPreset?.metric?.ordering === "ASC" ? "asc" : "desc",
									},
								]);
							}
						}}
					/>
				</AuthorizationGuard>
				<div className="min-h-0 min-w-0 grow flex flex-col rounded-l-none rounded-md bg-white px-4 pt-6 pb-2 ">
					<div className="min-h-0 grow flex flex-col overflow-y-auto">
						<AuthorizationGuard requiredService="BULK_ANALYSIS">
							<div className="flex justify-between items-center mb-2">
								<ButtonGroupRadio
									size="small"
									value={viewMode}
									onChange={setViewMode}
									options={[
										{
											"data-qualifier": `BulkAnalysis/SetViewMode(${PortfolioListViewMode.mixed})`,
											value: PortfolioListViewMode.mixed,
											icon: "toolchart-tablegraph",
										},
										{
											"data-qualifier": `BulkAnalysis/SetViewMode(${PortfolioListViewMode.chart})`,
											value: PortfolioListViewMode.chart,
											icon: "toolchart-cart",
										},
										{
											className: "justify-center rounded-r-lg",
											"data-qualifier": `BulkAnalysis/SetViewMode(${PortfolioListViewMode.table})`,
											value: PortfolioListViewMode.table,
											icon: "toolchart-table",
										},
									]}
								/>
								<div>
									{queryInstrumentsRows.data?.total != null ? (
										<Pagination
											skip={skip.value}
											take={take.value}
											total={queryInstrumentsRows.data?.total ?? 0}
											onChange={skip.set}
										/>
									) : (
										"..."
									)}
								</div>
							</div>
						</AuthorizationGuard>

						{!hasAccess(user, { requiredService: "BULK_ANALYSIS" }) && (
							<div className="flex justify-between items-center mb-4">
								<DebouncedSearchInput
									externalQuery={insightPanelPreset?.search ?? ""}
									onChange={(search) => {
										query.set(search);
										setInsightPanelPreset((preset) => ({ ...preset, search }));
									}}
									placeholder="search portfolio by name"
									data-qualifier="InsightPanel/SearchByKeyWord"
									style={{ width: 692 }}
									className="mb-4"
								/>
								<div>
									{queryInstrumentsRows.data?.total != null ? (
										<Pagination
											skip={skip.value}
											take={take.value}
											total={queryInstrumentsRows.data?.total ?? 0}
											onChange={skip.set}
										/>
									) : (
										"..."
									)}
								</div>
							</div>
						)}

						<ReactQueryWrapperBase query={queryInstrumentsRows} loadingFallback={<IconWalls.LoadingData />}>
							{() => (
								<div className="min-h-0 grow flex flex-col relative mb-2">
									<LocalOverlay
										show={queryInstrumentsRows.isFetching || metricResultQuery.isFetching}
										className="z-10"
										style={{
											background: "transparent",
										}}
									>
										<CircularProgressBar value="indeterminate" />
									</LocalOverlay>

									<div
										className={toClassName({
											hidden: viewMode === PortfolioListViewMode.table,
											"h-full": viewMode === PortfolioListViewMode.chart || viewMode === PortfolioListViewMode.mixed,
										})}
									>
										<Chart
											key={viewMode}
											chartRef={chartRef}
											thresholdValue={insightPanelPreset?.metric?.keyMetricThreshold?.first()?.thresholdValue}
											multiSelectCtx={rowSelection}
											metricResultsById={metricResultsById}
											onThresholdChange={portfolioInsightHandleRef.current?.setThreshold}
											title={
												!metricKey
													? "-"
													: (metricParameters?.classificationUuid && metricParameters?.option) ||
														metricParameters?.riskModelExposureAssetClass ||
														t(`METRIC_COMPARISON_CHART.${metricKey}`)
											}
											chartHeight="full"
											unit={
												insightPanelPreset?.metric?.key
													? unitOfMeasurement.portfolioList[insightPanelPreset?.metric?.key]
													: undefined
											}
											onChangeSelected={(selected) => {
												const selectedRows: IRowNode<InvestmentItem>[] = [];
												gridEl?.api.forEachNode((node) => {
													if (node.data?.uuid && selected.has(node.data.uuid)) {
														selectedRows.push(node);
													}
												});

												gridEl?.api.deselectAll();
												gridEl?.api.setNodesSelected({
													nodes: selectedRows,
													newValue: true,
												});
											}}
										/>
									</div>
									{viewMode === PortfolioListViewMode.mixed && <br />}
									<div
										className={toClassName({
											"min-h-0 grow shrink-0 flex flex-col":
												viewMode === PortfolioListViewMode.mixed || viewMode === PortfolioListViewMode.table,
											"basis-1/2": viewMode === PortfolioListViewMode.mixed,
											hidden: viewMode === PortfolioListViewMode.chart,
										})}
									>
										<PortfolioTable
											sortedRows={rows}
											rowSelection={rowSelection}
											columns={columns}
											isMetricResultLoading={
												metricResultQuery.isLoading && hasAccess(user, { requiredService: "BULK_ANALYSIS" })
											}
											agGridRef={setGridEl}
											agContext={{ keyMetricHeaderName }}
										/>
									</div>
								</div>
							)}
						</ReactQueryWrapperBase>
					</div>
				</div>
			</div>
			<PortfolioToolbar
				gridEl={gridEl}
				selectableRows={queryInstrumentsRows.data?.total ?? 0} //projectedRowsMap.size
				rowSelection={rowSelection}
				onApplyFilters={onApplyFilters}
				portfolioBulkActions={portfolioBulkActions}
				filteredProjectedRowsMap={filteredProjectedRowsMap}
				disable={queryProjectInvestmentList.isFetching}
				onChangeSelection={(mode) => {
					if (mode === "unselect-all") {
						isRestoringSelection.current = true;
						gridEl?.api.deselectAll();
						isRestoringSelection.current = false;
						return;
					}

					if (mode === "select-all") {
						isRestoringSelection.current = true;
						gridEl?.api.selectAll();
						isRestoringSelection.current = false;
						return;
					}
				}}
			/>
			<CompareOverlay
				show={comparedInvestments.length > 0}
				onClose={() => setComparedInvestments([])}
				onRemove={(id) => setComparedInvestments((v) => v.filter((x) => x.id !== id))}
				compareData={comparedInvestments}
			/>
		</>
	);
}

/** @internal — exported for tests */
export function createMetricCellValue(
	investment: ListInvestmentsDTO,
	metrics: MetricResultEntry[],
	metricKey?: FieldKey,
): string | undefined {
	const metricValue = metrics.find(({ uuid }) => uuid === investment.uuid)?.value;
	if (metricValue && metricKey) {
		const sign = unitOfMeasurement.portfolioList[metricKey];
		return `${formatNumber(metricValue, 2)}${sign}`;
	}

	return undefined;
}

/** @internal — exported for tests */
export function PortfolioToolbar(
	props: Readonly<{
		gridEl: AgGridReact<InvestmentItem> | null;
		rowSelection: MultiSelectCtx<string>;
		selectableRows: number;
		portfolioBulkActions: BulkAnalysisActions;
		onApplyFilters(investments: Set<string>): void;
		filteredProjectedRowsMap: Immutable.Map<string, InvestmentProjectedListEntry>;
		disable?: boolean;
		onChangeSelection?(mode: "select-all" | "unselect-all"): void;
	}>,
) {
	const { rowSelection, onApplyFilters, portfolioBulkActions, selectableRows } = props;

	const { t } = useTranslation();
	const user = useUserValue();
	const { push } = useTypedNavigation();
	const canSendToExecution = useCanSendToExecution();

	const handler = useUnsafeUpdatedRef({
		push,
		onApplyFilters,
		portfolioBulkActions,
	});

	const queryExportTemplates = reportRepository.exportTemplates.useQuery();
	const exportTemplates = queryExportTemplates.data ?? stableEmptyArray;

	const queryCommentaryTemplates = storyfolioRepository.templates.useQuery();

	const commentaryTemplateOptions = useMemo<Array<Option<string>>>(() => {
		const commentaryTemplates = queryCommentaryTemplates.data;
		if (!commentaryTemplates) {
			return [];
		}
		return commentaryTemplates.flatMap((x) =>
			x.visible && x.uuid && x.name ? [{ label: x.name, value: x.uuid }] : [],
		);
	}, [queryCommentaryTemplates]);

	const selection = useMemo(() => rowSelection.selection, [rowSelection]);

	const actions = useMemo<Array<BulkActionItem>>(
		() => [
			{
				label: "Manage selection",
				dropdownItems: [
					{
						label: selectableRows === 1 ? `Select one portfolio` : `Select all ${selectableRows} portfolios`,
						onClick() {
							trackMixPanelEvent("portfolio.bulkAnalysis.selectAll", {
								selectedPortfolios: selectableRows,
							});
							rowSelection.setSelection(rowSelection.selection.merge(props.filteredProjectedRowsMap.keys()));
							props.onChangeSelection?.("select-all");
						},
					},
					{
						label: `Unselect`,
						onClick() {
							rowSelection.reset();
							trackMixPanelEvent("portfolio.bulkAnalysis.unselectAll", null);
							props.onChangeSelection?.("unselect-all");
						},
					},
				],
			},
			...(hasAccess(user, { requiredService: "BULK_ANALYSIS" })
				? [
						{
							label: "Apply selection to filter",
							icon: "Filter-no" as const,
							onClick: () => handler.current.onApplyFilters(selection),
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(ApplyFilter)",
							disabled: props.disable,
						} satisfies BulkActionDropdownItem,
					]
				: []),

			// delete
			{
				label: "Delete",
				icon: "Delete" as const,
				onClick: () => portfolioBulkActions.delete(),
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Delete)",
			},

			// download
			{
				label: "Download",
				icon: "Dowload" as const,
				dropdownItems: [
					...exportTemplates.map(
						(template) =>
							({
								icon: exportTemplateIcon(template),
								label: exportTemplateLabel(template),
								onClickAsync: () => portfolioBulkActions.downloadReport(template),
								"data-qualifier": `PortfolioStudio/PortfolioList/BatchAction(Download)/Report(${template.templateName})`,
							}) satisfies BulkActionDropdownItem,
					),

					...(hasAccess(user, { requiredService: "EXPORT" })
						? (user.exportFormats ?? []).map(
								(format) =>
									({
										icon: "xls",
										onClickAsync: () => portfolioBulkActions.downloadComposition(format),
										label: `${t(`EXPORT.${format}`)} template`,
										"data-qualifier": `PortfolioStudio/PortfolioList/BatchAction(Download)/Composition(${format})`,
									}) satisfies BulkActionDropdownItem,
							)
						: []),
					...(hasAccess(user, {
						requiredService: "INVESTMENTS_REPORT_TEMPLATE_EDITOR",
					})
						? [
								{
									icon: "Settings" as const,
									label: "Report builder",
									onClick: () =>
										handler.current.push("PortfolioStudioSettings", {
											tab: PortfolioStudioSettingTabEnum.ReportCustomisation,
										}),
								},
							]
						: []),
				],
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Download)",
			},

			// compare
			{
				label: "Compare",
				icon: "compare" as const,
				onClick: () => portfolioBulkActions.compare(),
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Compare)",
			},

			//create proposal
			{
				label: "Create proposal",
				icon: "Enhance" as const,
				onClickAsync: () => portfolioBulkActions.createProposal(),
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(CreateProposal)",
			},

			//edit portfolio settings
			{
				label: "Edit market view",
				icon: "Settings" as const,
				onClickAsync: () => portfolioBulkActions.editSettings(),
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(EditPortfolioSettings)",
			},

			// merge (aka: create new portfolio with editor)
			{
				label: "Merge",
				icon: "merge" as const,
				// biome-ignore lint/correctness/useHookAtTopLevel: not a hook
				onClick: () => portfolioBulkActions.useAsMixedPortfolio(),
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(MergeComposition)",
			},

			//generate commentary
			{
				label: "Generate commentary",
				icon: "sphere-ai" as const,
				dropdownItems: commentaryTemplateOptions.map<BulkActionDropdownItem>((commentaryTemplate) => ({
					label: commentaryTemplate.label,
					onClickAsync: () => portfolioBulkActions.generateCommentary(commentaryTemplate.value),
				})),
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(GenerateCommentary)",
			},

			// manage proposal
			{
				label: "Manage proposal",
				icon: "Suggestion" as const,
				dropdownItems: [
					{
						label: "Accept proposal",
						dropdownItems: [
							{
								label: "Accept proposal",
								onClickAsync: () => portfolioBulkActions.acceptProposal(),
							},
							{
								label: "Accept in the past",
								onClickAsync: () => portfolioBulkActions.acceptProposalInThePast(),
							},
						],
					},
					{
						label: "Reject proposal",
						onClickAsync: () => portfolioBulkActions.rejectProposal(),
					},
					// Only where Sphere has somewhere to send an executed proposal — the same gate the
					// single-portfolio Execute and Stop execution buttons use. Both flips of that switch sit
					// last so that accept and reject stay side by side.
					...(canSendToExecution
						? [
								{
									label: "Send to execution",
									onClickAsync: () => portfolioBulkActions.sendToExecution(),
								} satisfies BulkActionDropdownItem,
								{
									label: "Stop execution",
									onClickAsync: () => portfolioBulkActions.stopExecution(),
								} satisfies BulkActionDropdownItem,
							]
						: []),
				],
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(ManageProposal)",
			},
			{
				label: "Share",
				icon: "share" as const,
				onClickAsync: () => portfolioBulkActions.share(),
				disabled: props.disable,
				"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Share)",
			},
		],
		[
			user,
			selection,
			exportTemplates,
			commentaryTemplateOptions,
			canSendToExecution,
			handler,
			t,
			portfolioBulkActions,
			props.disable,
			props.filteredProjectedRowsMap.keys,
			props.onChangeSelection,
			rowSelection.reset,
			rowSelection.selection.merge,
			rowSelection.setSelection,
			selectableRows,
		],
	);

	return (
		<BulkActions
			variant="floating"
			count={rowSelection.selection.size}
			actions={actions}
			onClear={() => {
				props.gridEl?.api.deselectAll();
				rowSelection.reset();
			}}
		/>
	);
}

type PortfolioTableProps = {
	rowSelection: MultiSelectCtx<string>;
	columns: ColDef<InvestmentItem>[];
	sortedRows: InvestmentItem[];
	keyMetricColumn?: ColDef<InvestmentItem>;
	isMetricResultLoading: boolean;
	agGridRef?: Ref<AgGridReact<InvestmentItem>>;
	agContext?: GridOptions<InvestmentItem>["context"];
};

/** @internal — exported for tests */
export const canEnhanceInvestment = (status?: InvestmentStatus) => status === "READY" || status === "ACCEPTED";
function PortfolioTable({
	rowSelection,
	columns,
	sortedRows,
	isMetricResultLoading,
	agGridRef,
	agContext,
}: PortfolioTableProps) {
	const { aclSpawn } = useUpdateAccessListControlDialog("INVESTMENT");
	const user = useUserValue();
	const { push } = useTypedNavigation();

	const getContextMenuItems = useCallback<GetContextMenuItems<InvestmentItem, any>>(
		(row) => {
			const portfolio = row.node?.data;
			if (!portfolio) {
				return ["copy"];
			}

			const canEnhance = canEnhanceInvestment(portfolio.status);
			const entityManagementActions = portfolioEntityManagementActions(user, portfolio);

			return [
				...(aclByArea.portfolio.canCreateProposal(user.id, portfolio.richAcl?.acl ?? [])
					? [
							{
								icon: canEnhance
									? renderToStaticMarkup(<Icon icon="Enhance" size={16} />)
									: renderToStaticMarkup(<Icon icon="Enhance-Not-available" size={16} />),
								disabled: !canEnhance,
								action: () => {
									const intentId = generateIntentId();
									trackMixPanelEvent("portfolio.entityActions.createProposalIntent", {
										intentId,
										portfolio: {
											uuid: portfolio.uuid!,
											name: portfolio.name!,
										},
									});
									push("Portfolios/EditPortfolio", {
										portfolioUid: portfolio.uuid!,
										intentId,
									});
								},
								menuItemParams: {
									"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Enhance)",
								},
								name: "Create proposal",
							},
						]
					: []),
				{
					icon: renderToStaticMarkup(<Icon icon="Delete" size={16} />),
					disabled: !entityManagementActions.deleteAsync,
					action: unpromisify(entityManagementActions.deleteAsync ?? noop),
					menuItemParams: {
						"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Delete)",
					},
					name: "Delete",
				},
				{
					icon: renderToStaticMarkup(<Icon icon="Content-Copy" size={16} />),
					disabled: !entityManagementActions.duplicateAsync,
					action: unpromisify(entityManagementActions.duplicateAsync ?? noop),
					menuItemParams: {
						"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Duplicate)",
					},
					name: "Duplicate",
				},
				{
					icon: renderToStaticMarkup(<Icon icon="Edit" size={16} />),
					action: unpromisify(entityManagementActions.renameAsync ?? noop),
					menuItemParams: {
						"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Rename)",
					},
					name: "Rename",
				},
				...(validateACLPermissions(user.id, portfolio.richAcl?.acl ?? [], roleByArea.portfolio.EDITOR)
					? [
							{
								icon: renderToStaticMarkup(<Icon icon="share" size={16} />),
								action: unpromisify(() => aclSpawn(portfolio.name, portfolio.uuid)),
								menuItemParams: {
									"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Share)",
								},
								name: "Share",
							},
						]
					: []),
			];
		},
		[user, push, aclSpawn],
	);
	const persistColProps = agExtra.usePersistColState(persistedColStateKeys["tableV3-portfolioList"]);

	if (isMetricResultLoading) {
		return <ProgressBar value="indeterminate" />;
	}

	if (!columns) {
		return <></>;
	}

	return (
		<AgGridWithModules<InvestmentItem>
			{...agExtra.checkboxColumn({
				mode: "multiRow",
				selectAll: "currentPage",
			})}
			{...persistColProps}
			getRowId={(row) => row.data.uuid!}
			columnDefs={columns}
			rowData={sortedRows}
			getContextMenuItems={getContextMenuItems}
			innerRef={agGridRef}
			context={agContext}
		/>
	);
}

function Chart(props: {
	chartRef?: ForwardedRef<HighchartsReactRefObject>;
	unit?: string;
	metricResultsById: Map<string, MetricResultEntry>;
	multiSelectCtx: MultiSelectCtx<string>;
	onChangeSelected?(selected: Set<string>): void;
	thresholdValue?: number;
	onThresholdChange?(newThreshold: number): void;
	title: string;
	chartHeight?: MetricComparisonChartProps["chartHeight"];
}) {
	const [highlightProposal, setHighlightProposal] = useState(false);
	const selected = props.multiSelectCtx.selection.toSet();
	const setSelected = props.multiSelectCtx.setSelection;
	const { title, thresholdValue, onThresholdChange } = props;

	const metricComparisonData = useDebouncedMemo<MetricComparisonChartProps["data"]>(
		() =>
			props.metricResultsById
				.valueSeq()
				.toArray()
				.map((metric): MetricComparisonChartProps["data"][number] => ({
					id: metric.uuid!,
					name: metric.name ?? "Missing portfolio name",
					metrics: [metric.value ?? null, metric.enhancementValue ?? null],
				})),
		[props.metricResultsById],
	);

	return (
		<MetricComparisonChart
			actions={
				<Switch checked={highlightProposal} onChange={setHighlightProposal}>
					Highlight proposal
				</Switch>
			}
			highlightSecondaryMetric={highlightProposal}
			mainMetricLabel="Current"
			chartTitle={title}
			secondaryMetricLabel="Proposal"
			unit={props.unit}
			threshold={thresholdValue}
			thresholdDragStep={0.01}
			selectedIds={selected}
			onSelectionChange={(selected) => {
				props.onChangeSelected?.(selected);
				setSelected(selected);
			}}
			onThresholdChange={onThresholdChange}
			data={metricComparisonData.value}
			chartHeight={props.chartHeight}
			innerRef={props.chartRef}
		/>
	);
}

function BulkAnalysisTransitions(props: {
	children: ReactNode;
	in: boolean;
	onTransitionChange?(status: TransitionStatus): void;
	className?: ClassList;
	unmountOnExit?: boolean;
}) {
	return (
		<Transition in={props.in} onChange={(status) => props.onTransitionChange?.(status)}>
			<NestedTransition
				className={overrideClassList(
					"max-w-full h-full transition-all transition-discrete ease-in-out duration-400 flex flex-col",
					props.className,
				)}
				enterFromClassName={"opacity-0"}
				enterToClassName={"opacity-100"}
				exitedClassName="invisible z-0 overflow-y-hidden"
				unmountOnExit={props.unmountOnExit}
			>
				{({ className: animatedClassList, style: animatedStyle, transitionStatus }) => (
					<div
						data-transitionstatus={transitionStatus}
						className={toClassName(animatedClassList)}
						style={animatedStyle}
					>
						{props.children}
					</div>
				)}
			</NestedTransition>
		</Transition>
	);
}

export default PortfolioList;
